import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
} from "@/components/ui/dropdown-menu";

export default function NotificationsBell() {
    const { user } = useAuth();
    const { t, lang } = useLang();
    const [data, setData] = useState({ items: [], unread: 0 });

    const refresh = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications");
            setData(data);
        } catch {}
    }, [user]);

    useEffect(() => {
        refresh();
        if (!user) return;
        const iv = setInterval(refresh, 30000);
        return () => clearInterval(iv);
    }, [user, refresh]);

    const markAllRead = async () => {
        await api.post("/notifications/read-all");
        refresh();
    };

    if (!user) return null;

    return (
        <DropdownMenu onOpenChange={(o) => o && refresh()}>
            <DropdownMenuTrigger asChild>
                <button className="relative rounded-full h-10 w-10 grid place-items-center hover:bg-surface transition-colors" data-testid="notifications-btn" aria-label={t("notifications")}>
                    <Bell className="w-5 h-5" />
                    {data.unread > 0 && (
                        <span className="absolute top-1 end-1 bg-terracotta text-white text-[9px] font-bold rounded-full h-4 min-w-[16px] px-1 grid place-items-center" data-testid="notifications-badge">
                            {data.unread}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0 max-h-[70vh] overflow-hidden">
                <div className="p-3 border-b border-border flex items-center justify-between">
                    <div className="font-bold text-espresso">{t("notifications")}</div>
                    {data.unread > 0 && (
                        <button onClick={markAllRead} className="text-xs text-terracotta font-bold hover:underline" data-testid="mark-all-read-btn">
                            {t("markAllRead")}
                        </button>
                    )}
                </div>
                <div className="overflow-y-auto max-h-[60vh]">
                    {data.items.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-10">
                            {t("noNotifications")}
                        </div>
                    ) : (
                        data.items.map((n) => (
                            <Link
                                key={n.notification_id}
                                to={n.link || "#"}
                                className={`block p-3 border-b border-border/60 hover:bg-surface transition-colors ${n.read ? "" : "bg-terracotta/5"}`}
                                data-testid={`notification-${n.notification_id}`}
                            >
                                <div className="flex items-start gap-3">
                                    {n.image && <img src={n.image} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm text-espresso line-clamp-1">
                                            {lang === "ar" ? n.title_ar : n.title_en}
                                        </div>
                                        <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>
                                        <div className="text-[10px] text-muted-foreground/70 mt-1">
                                            {new Date(n.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en")}
                                        </div>
                                    </div>
                                    {!n.read && <div className="w-2 h-2 rounded-full bg-terracotta mt-1" />}
                                </div>
                            </Link>
                        ))
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
